import { warmBackend } from './auth';
import { enqueue, isOfflineable } from './offlineQueue';
import { toast } from 'sonner';

export const API_BASE = '/api';

export function getLocalDate(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getLocalTime(): string {
  const now = new Date();
  const h = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  return `${h}:${min}`;
}

export function localDateFromTimestamp(ts: string): string {
  if (ts.length >= 10 && !ts.includes('T')) return ts.slice(0, 10);
  const d = new Date(ts);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function computeStreakFromEntries(entries: Array<{ timestamp: string }>): number {
  const today = getLocalDate();
  const ydObj = new Date();
  ydObj.setDate(ydObj.getDate() - 1);
  const yesterday = `${ydObj.getFullYear()}-${String(ydObj.getMonth() + 1).padStart(2, '0')}-${String(ydObj.getDate()).padStart(2, '0')}`;
  const uniqueDays = Array.from(new Set(entries.map(e => localDateFromTimestamp(e.timestamp)))).sort().reverse();
  if (uniqueDays.length === 0) return 0;
  const mostRecent = uniqueDays[0];
  if (mostRecent !== today && mostRecent !== yesterday) return 0;
  let cursor = mostRecent;
  let count = 0;
  for (const day of uniqueDays) {
    if (day === cursor) {
      count++;
      const d = new Date(cursor + 'T12:00:00');
      d.setDate(d.getDate() - 1);
      cursor = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    } else if (day < cursor) {
      break;
    }
  }
  return count;
}

/** Compute the rounded average of a numeric field across entries, skipping nullish values. */
export function avgOfField<T>(entries: T[], getter: (e: T) => number | null | undefined): number | null {
  const values: number[] = [];
  for (const e of entries) {
    const v = getter(e);
    if (v != null) values.push(v);
  }
  if (values.length === 0) return null;
  return Math.round(values.reduce((s, v) => s + v, 0) / values.length * 10) / 10;
}

/** Wait for any in-flight DB warm-up, then fetch with 503 retry (Neon cold-start). */
export async function fetchWithRetry(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  await warmBackend();
  const res = await fetch(input, init);
  if (res.status === 503) {
    const retryAfter = parseInt(res.headers.get('Retry-After') || '3', 10);
    await new Promise(r => setTimeout(r, retryAfter * 1000));
    return fetch(input, init);
  }
  return res;
}

export async function apiPost(path: string, body: object, token?: string | null): Promise<boolean> {
  try {
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetchWithRetry(`${API_BASE}${path}`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(body),
    });
    return res.ok;
  } catch {
    if (isOfflineable(path)) {
      enqueue({ method: 'POST', path: `${API_BASE}${path}`, body, token: token ?? null });
      toast('Saved offline \u2014 will sync when you reconnect', { icon: '\u2601\ufe0f' });
      return true;
    }
    return false;
  }
}

export async function apiPut(path: string, body?: object | null, token?: string | null): Promise<boolean> {
  try {
    const headers: HeadersInit = {};
    if (body) headers['Content-Type'] = 'application/json';
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetchWithRetry(`${API_BASE}${path}`, {
      method: 'PUT',
      headers,
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    });
    return res.ok;
  } catch {
    if (isOfflineable(path)) {
      enqueue({ method: 'PUT', path: `${API_BASE}${path}`, body: body ?? null, token: token ?? null });
      toast('Saved offline \u2014 will sync when you reconnect', { icon: '\u2601\ufe0f' });
      return true;
    }
    return false;
  }
}

export async function apiGet<T>(path: string, token?: string | null): Promise<T | null> {
  try {
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetchWithRetry(`${API_BASE}${path}`, { headers, credentials: 'include' });
    if (!res.ok) return null;
    return res.json() as Promise<T>;
  } catch {
    return null;
  }
}
